import React, { useState, useEffect } from 'react';
import { Input } from '../ui/input';
import { Label } from '../ui/label';
import { Upload, FileText, Check, CreditCard, ShieldCheck } from 'lucide-react';
import { apiRequest } from '../../lib/utils';
import { toast } from 'sonner';

const DOCS = [
  { key: 'certificate', label: 'Medical Council Registration', desc: 'MCI / State Council Certificate', required: true },
  { key: 'degree', label: 'Degree Certificate', desc: 'MBBS / MD / BDS / GNM etc.', required: true, backendKey: 'certificate' }, // Uses certificates[0]
  { key: 'id_proof', label: 'Government ID', desc: 'Aadhar / PAN Card', required: true },
  { key: 'photo', label: 'Passport Photo', desc: 'Recent clear photograph', required: true },
];

const PAYOUT_METHODS = [
  { value: 'bank_transfer', label: 'Bank Transfer (NEFT/IMPS)' },
  { value: 'upi', label: 'UPI' },
];

export default function Step7Documents({ data = {}, onSave, onChange }) {
  const [uploaded, setUploaded] = useState({});
  const [uploading, setUploading] = useState(null);
  const [bank, setBank] = useState({
    account_holder: '', account_number: '', ifsc: '', bank_name: '', upi_id: '',
    payout_method: 'bank_transfer', clinic_managed_payout: false,
    ...(data?.bank_details || {}),
  });

  useEffect(() => {
    setUploaded({
      certificate: data?.registration_cert || null,
      degree: data?.degree_cert || null,
      id_proof: data?.id_proof || null,
      photo: data?.avatar || null,
    });
    if (data?.bank_details) setBank(prev => ({ ...prev, ...data.bank_details }));
  }, [data]);

  const handleFile = async (doc, file) => {
    if (!file) return;
    setUploading(doc.key);
    try {
      const formData = new FormData();
      formData.append('file', file);
      // Map "degree" to backend "certificate" (goes to certificates[0])
      // Actual Medical Council Reg goes to certificates[1] via "registration_cert"
      const backendType = doc.key === 'degree' ? 'certificate' : doc.key === 'certificate' ? 'registration_cert' : doc.key;
      const res = await apiRequest(`/provider/upload-document?doc_type=${backendType}`, {
        method: 'POST',
        body: formData,
        headers: {},
      });
      const url = res.url || res.file_url || true;
      setUploaded(prev => ({ ...prev, [doc.key]: url }));
      // Bubble up to wizard formData so Step 8 Review sees latest uploads.
      // Wizard formData.documents uses keys: registration_cert, degree_cert, id_proof, avatar.
      const wizardKeyMap = { certificate: 'registration_cert', degree: 'degree_cert', id_proof: 'id_proof', photo: 'avatar' };
      const wizardKey = wizardKeyMap[doc.key];
      if (wizardKey && onChange) {
        onChange({ ...data, [wizardKey]: url, bank_details: bank });
      }
      toast.success(`${doc.label} uploaded`);
    } catch (e) {
      toast.error(e.message || 'Upload failed');
    } finally {
      setUploading(null);
    }
  };

  const updateBank = (k, v) => setBank(p => ({ ...p, [k]: v }));

  const handleSubmit = (e) => {
    e.preventDefault();
    onSave({ bank_details: bank });
  };

  return (
    <form id="step-form-7" onSubmit={handleSubmit} className="p-6 lg:p-8 space-y-8">
      {/* Documents */}
      <section>
        <h3 className="text-lg font-semibold text-slate-900 flex items-center gap-2 mb-1"><ShieldCheck className="w-5 h-5 text-[#3B1C73]" /> Verification Documents</h3>
        <p className="text-sm text-slate-500 mb-4">Upload clear scans/photos. Accepted: PDF, JPG, PNG (max 5 MB each)</p>
        <div className="grid sm:grid-cols-2 gap-3">
          {DOCS.map(doc => {
            const done = uploaded[doc.key];
            const isUploading = uploading === doc.key;
            return (
              <label key={doc.key} className={`relative p-4 rounded-xl border-2 cursor-pointer transition-all ${done ? 'border-emerald-300 bg-emerald-50/50' : 'border-dashed border-slate-300 hover:border-[#3B1C73] hover:bg-[#3B1C73]/5'}`}>
                <input type="file" accept=".pdf,.jpg,.jpeg,.png" className="hidden" onChange={(e) => handleFile(doc, e.target.files[0])} disabled={isUploading} data-testid={`doc-upload-${doc.key}`} />
                <div className="flex items-start gap-3">
                  <div className={`w-10 h-10 rounded-lg flex items-center justify-center flex-shrink-0 ${done ? 'bg-emerald-500 text-white' : 'bg-slate-100 text-slate-500'}`}>
                    {isUploading ? <div className="w-4 h-4 animate-spin rounded-full border-2 border-white border-t-transparent" /> : done ? <Check className="w-5 h-5" /> : <Upload className="w-5 h-5" />}
                  </div>
                  <div className="flex-1 min-w-0">
                    <div className="flex items-center gap-1 flex-wrap">
                      <p className="font-semibold text-slate-900 text-sm">{doc.label}</p>
                      {doc.required && <span className="text-xs text-red-500">*</span>}
                    </div>
                    <p className="text-xs text-slate-500">{doc.desc}</p>
                    <p className={`text-xs mt-1 font-medium ${done ? 'text-emerald-600' : 'text-[#3B1C73]'}`}>
                      {isUploading ? 'Uploading...' : done ? '✓ Uploaded — Click to replace' : 'Click to upload'}
                    </p>
                  </div>
                </div>
              </label>
            );
          })}
        </div>
      </section>

      {/* Bank Details */}
      <section>
        <h3 className="text-lg font-semibold text-slate-900 flex items-center gap-2 mb-1"><CreditCard className="w-5 h-5 text-[#3B1C73]" /> Bank Details for Payouts</h3>
        <p className="text-sm text-slate-500 mb-4">Patient payments will be transferred to this account. Kept confidential.</p>

        <label className="flex items-start gap-3 p-4 mb-4 bg-amber-50 border border-amber-200 rounded-xl cursor-pointer">
          <input type="checkbox" checked={bank.clinic_managed_payout} onChange={(e) => updateBank('clinic_managed_payout', e.target.checked)} className="w-5 h-5 accent-[#3B1C73] mt-0.5" />
          <div>
            <p className="font-medium text-slate-900 text-sm">Payments managed by clinic/hospital</p>
            <p className="text-xs text-slate-600 mt-1">If ticked, payouts will go to the clinic admin account instead of yours. Leave unchecked if you receive payments directly.</p>
          </div>
        </label>

        <div className="p-5 rounded-xl border border-slate-200 bg-white space-y-4">
          <div className="grid sm:grid-cols-2 gap-3">
            <div>
              <Label>Payout Method</Label>
              <select value={bank.payout_method} onChange={(e) => updateBank('payout_method', e.target.value)} className="mt-1.5 w-full px-3 py-2 border border-slate-300 rounded-lg text-sm">
                {PAYOUT_METHODS.map(m => <option key={m.value} value={m.value}>{m.label}</option>)}
              </select>
            </div>
            <div><Label>Account Holder Name *</Label><Input required value={bank.account_holder} onChange={(e) => updateBank('account_holder', e.target.value)} placeholder="As per bank records" className="mt-1.5" data-testid="bank-holder" /></div>
          </div>

          {bank.payout_method === 'bank_transfer' ? (
            <div className="grid sm:grid-cols-2 gap-3">
              <div><Label>Bank Name</Label><Input value={bank.bank_name} onChange={(e) => updateBank('bank_name', e.target.value)} placeholder="HDFC / SBI / ICICI" className="mt-1.5" /></div>
              <div><Label>IFSC Code *</Label><Input required value={bank.ifsc} onChange={(e) => updateBank('ifsc', e.target.value.toUpperCase())} placeholder="HDFC0001234" maxLength={11} className="mt-1.5" data-testid="bank-ifsc" /></div>
              <div className="sm:col-span-2"><Label>Account Number *</Label><Input required value={bank.account_number} onChange={(e) => updateBank('account_number', e.target.value.replace(/\D/g, ''))} placeholder="Enter account number" className="mt-1.5" data-testid="bank-account" /></div>
            </div>
          ) : (
            <div>
              <Label>UPI ID *</Label>
              <Input required value={bank.upi_id} onChange={(e) => updateBank('upi_id', e.target.value)} placeholder="yourname@paytm / @okhdfc / @ybl" className="mt-1.5" data-testid="bank-upi" />
              <p className="text-xs text-slate-500 mt-1">Fast, instant transfers via UPI</p>
            </div>
          )}
        </div>
      </section>

      {/* Verification Status */}
      <section className="p-4 bg-blue-50 border border-blue-200 rounded-xl flex items-start gap-3">
        <FileText className="w-5 h-5 text-blue-600 flex-shrink-0 mt-0.5" />
        <div>
          <p className="font-semibold text-slate-900 text-sm">What happens next?</p>
          <ul className="text-xs text-slate-600 mt-1 space-y-0.5">
            <li>1. Admin will review your documents within 24 hours</li>
            <li>2. Once approved, you&apos;ll receive verification badge + start getting patient bookings</li>
            <li>3. Payouts start after your first appointment completion</li>
          </ul>
        </div>
      </section>
    </form>
  );
}
